import { LinearProgress, Stack, Typography } from '@mui/joy'
import { useStore } from 'effector-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { Element } from 'react-scroll'
import { roadAccidentModel } from 'src/entities/RoadAccident'
import {
  editRoadAccidentModel,
  EditRoadAccident,
  Status,
} from 'src/features/RoadAccidents/EditRoadAccident'
import { usePermissions } from '~/entities/viewer'
import { formatDateTimeForUI } from '~/shared/lib/date'
import { myPalette } from '~/shared/lib/theme'
import { Breadcrumbs, BreadcrumbsList } from '~/shared/ui/Breadcrumbs'
import { Button, LinkDecorator } from '~/shared/ui/Buttons'
import { PlusIcon } from '~/shared/ui/Icons'
import {
  Container,
  PageTitle,
  PageContainer,
  ContainerTitle,
} from '~/shared/ui/Layout'
import { NotFound } from '~/shared/ui/NotFound'
import {
  QuickNavigation,
  QuickNavigationOptions,
} from '~/shared/ui/QuickNavigation'
import { AuditsModal } from '~/widgets/AuditsModal'

const quickNavigationOptions: QuickNavigationOptions = [
  { labelI18n: 'form.information', anchor: 'main' },
  { labelI18n: 'form.registration', anchor: 'registration' },
  { labelI18n: 'form.insurance', anchor: 'insurance' },
  { labelI18n: 'form.files', anchor: 'files' },
  { labelI18n: 'column.repair order', anchor: 'repairOrders' },
]

export const EditRoadAccidentPage = () => {
  const { t } = useTranslation()
  const params = useParams<{ roadAccidentId: string }>()
  const roadAccidentId = params.roadAccidentId as string

  const { canAuditsView, canRepairOrdersUpdate, canRoadAccidentsUpdate } =
    usePermissions()

  const isLoading = useStore(roadAccidentModel.requestFx.pending)
  const roadAccident = useStore(editRoadAccidentModel.$roadAccident)

  const number = roadAccident?.getNumber()
  const createdAt = roadAccident?.getCreatedAt()
  const inspectionId = roadAccident?.getInspection()?.getApiId()

  const repairOrder = roadAccident?.getRepairOrder()
  const repairOrderId = repairOrder?.getApiId()
  const repairOrdeNumber = repairOrder?.getNumber()

  const title = `№ ${number || '...'} ${t('from')} ${
    createdAt ? formatDateTimeForUI(createdAt) : ''
  }`

  const breadcrumbs: BreadcrumbsList = useMemo(
    () => [{ titleI18n: 'road accidents', link: '/road-accidents' }, { title }],
    [title],
  )

  const error = roadAccidentModel.useRoadAccidentError(roadAccidentId as string)
  if (!roadAccidentId || error) {
    return <NotFound message={t('alert.road accident load error')} />
  }

  return (
    <PageContainer title={t('road accident card')}>
      <Container isHeader>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
        >
          <Breadcrumbs list={breadcrumbs} lastLoading={isLoading} />
          <Button to={`/inspections/${inspectionId}`} variant='gray'>
            {t('form.inspection')}
          </Button>
        </Stack>
        <Stack direction='row' spacing={2}>
          <Status />
        </Stack>
        <Stack direction='row' alignItems='end' justifyContent='space-between'>
          <PageTitle loading={isLoading}>{title}</PageTitle>
          {canAuditsView && <AuditsModal auditableId={roadAccidentId} />}
        </Stack>
      </Container>

      <EditRoadAccident roadAccidentId={roadAccidentId} />

      <Element name='repairOrders'>
        <Container>
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='space-between'
          >
            <ContainerTitle>{t('form.repair order')}</ContainerTitle>
            {!isLoading && !repairOrder && (
              <Button
                show={canRepairOrdersUpdate && canRoadAccidentsUpdate}
                startDecorator={<PlusIcon />}
                to='repair-orders/new'
              >
                {t('button.create')}
              </Button>
            )}
          </Stack>
          {isLoading && <LinearProgress size='lg' />}

          {!isLoading && !repairOrder && (
            <Typography
              textAlign='center'
              fontSize={14}
              sx={{ color: myPalette.gray['600'] }}
            >
              {t('form.no data')}
            </Typography>
          )}
          {!isLoading && repairOrder && (
            <Typography
              startDecorator={
                <LinkDecorator
                  tooltipText={t('form.go to the work order card')}
                  to={`repair-orders/${repairOrderId}`}
                />
              }
            >
              № {repairOrdeNumber}
            </Typography>
          )}
        </Container>
      </Element>

      <QuickNavigation options={quickNavigationOptions} />
    </PageContainer>
  )
}
